import { d as colors } from "./focus-DaIphPOd.mjs";
import { c as transitions, d as spacing, f as zIndices, h as fonts, l as radii, m as fontWeights, p as fontSizes, u as shadows } from "./motion-Cg-JnSSP.mjs";
import { p as useVisualViewport } from "./hooks-ChkgkdFK.mjs";
import { r as Avatar } from "./ThemeSwatch-JAmwSMrS.mjs";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region molecules/Breadcrumbs.tsx
/**
* EITELKEIT Breadcrumbs
* ═══════════════════════════════════════════════════════════════════
* Navigation breadcrumb trail with mint hover state
*/
function BreadcrumbButton({ item, onNavigate }) {
	const [isHovered, setIsHovered] = React.useState(false);
	const handleClick = () => {
		if (item.href !== null && onNavigate !== null) onNavigate(item.href);
	};
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		style: {
			background: "none",
			border: "none",
			padding: 0,
			margin: 0,
			fontFamily: fonts.sans,
			fontSize: "14px",
			color: isHovered ? colors.mint : colors.textSecondary,
			cursor: "pointer",
			textDecoration: isHovered ? "underline" : "none",
			transition: transitions.all
		},
		onClick: handleClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		children: item.label
	});
}
function Breadcrumbs({ items, separator, onNavigate }) {
	const sep = separator ?? "/";
	const containerStyle = {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.sans,
		fontSize: "14px"
	};
	const separatorStyle = {
		color: colors.textMuted,
		userSelect: "none"
	};
	const currentItemStyle = {
		color: colors.textPrimary,
		fontFamily: fonts.sans,
		fontSize: "14px"
	};
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": "Breadcrumb",
		children: /* @__PURE__ */ jsx("div", {
			style: containerStyle,
			children: items.map((item, index) => {
				const isLast = index === items.length - 1;
				return /* @__PURE__ */ jsxs(React.Fragment, { children: [isLast ? /* @__PURE__ */ jsx("span", {
					style: currentItemStyle,
					children: item.label
				}) : /* @__PURE__ */ jsx(BreadcrumbButton, {
					item,
					onNavigate
				}), !isLast && /* @__PURE__ */ jsx("span", {
					style: separatorStyle,
					children: sep
				})] }, `${item.label}-${index}`);
			})
		})
	});
}
//#endregion
//#region molecules/Card.tsx
/**
* EITELKEIT Card
* ═══════════════════════════════════════════════════════════════════
* Container with glass variant
*/
const paddingMap = {
	none: "0",
	sm: "12px",
	md: "16px",
	lg: "24px"
};
function Card({ variant, padding, hover, style, children, ...props }) {
	const [isHovered, setIsHovered] = React.useState(false);
	const v = variant ?? "default";
	const p = padding ?? "md";
	const h = hover ?? false;
	const baseStyle = {
		borderRadius: radii.md,
		transition: transitions.all,
		padding: paddingMap[p]
	};
	const variants = {
		default: {
			background: colors.surface,
			border: `1px solid ${colors.border}`
		},
		elevated: {
			background: colors.surfaceElevated,
			border: `1px solid ${colors.border}`,
			boxShadow: shadows.md
		},
		glass: {
			background: colors.glassBg,
			backdropFilter: "blur(20px)",
			WebkitBackdropFilter: "blur(20px)",
			border: `1px solid ${colors.glassBorder}`,
			boxShadow: shadows.glass
		}
	};
	const hoverStyle = h && isHovered ? {
		borderColor: colors.borderHover,
		transform: "translateY(-2px)",
		boxShadow: shadows.lg
	} : {};
	return /* @__PURE__ */ jsx("div", {
		style: {
			...baseStyle,
			...variants[v],
			...hoverStyle,
			...style
		},
		onMouseEnter: () => h && setIsHovered(true),
		onMouseLeave: () => h && setIsHovered(false),
		...props,
		children
	});
}
Card.Header = function CardHeader({ style, children, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			marginBottom: "12px",
			...style
		},
		...props,
		children
	});
};
Card.Title = function CardTitle({ style, children, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			fontSize: "12px",
			fontWeight: 600,
			textTransform: "uppercase",
			letterSpacing: "0.1em",
			color: colors.textMuted,
			...style
		},
		...props,
		children
	});
};
Card.Content = function CardContent({ style, children, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		style,
		...props,
		children
	});
};
Card.Footer = function CardFooter({ style, children, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		style: {
			marginTop: "16px",
			paddingTop: "12px",
			borderTop: `1px solid ${colors.border}`,
			display: "flex",
			alignItems: "center",
			gap: "8px",
			...style
		},
		...props,
		children
	});
};
//#endregion
//#region molecules/NavItem.tsx
/**
* EITELKEIT NavItem
* ═══════════════════════════════════════════════════════════════════
* Navigation item with mint active state indicator
*/
function NavItem({ icon, label, active = false, badge, badgeVariant = "default", disabled = false, style, onClick, ...props }) {
	const [isHovered, setIsHovered] = React.useState(false);
	const containerStyle = {
		display: "flex",
		alignItems: "center",
		gap: "12px",
		padding: "10px 12px",
		borderRadius: radii.sm,
		fontFamily: fonts.sans,
		fontSize: "14px",
		color: active ? colors.mint : isHovered ? colors.textPrimary : colors.textSecondary,
		background: active ? colors.mintGlow : isHovered ? colors.surface : "transparent",
		cursor: disabled ? "not-allowed" : "pointer",
		transition: transitions.all,
		position: "relative",
		opacity: disabled ? .5 : 1,
		...style
	};
	const indicatorStyle = {
		position: "absolute",
		left: 0,
		top: "50%",
		transform: "translateY(-50%)",
		width: "3px",
		height: "24px",
		background: colors.mint,
		borderRadius: "0 2px 2px 0"
	};
	const badgeStyle = {
		marginLeft: "auto",
		padding: "2px 6px",
		borderRadius: "10px",
		fontSize: "10px",
		fontWeight: 600,
		background: badgeVariant === "danger" ? colors.danger : colors.surface,
		color: badgeVariant === "danger" ? colors.textPrimary : colors.textSecondary
	};
	return /* @__PURE__ */ jsxs("div", {
		style: containerStyle,
		onClick: disabled ? void 0 : onClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		...props,
		children: [
			active && /* @__PURE__ */ jsx("span", { style: indicatorStyle }),
			icon && /* @__PURE__ */ jsx("span", {
				style: {
					fontSize: "18px",
					width: "24px",
					textAlign: "center"
				},
				children: icon
			}),
			/* @__PURE__ */ jsx("span", { children: label }),
			badge !== void 0 && /* @__PURE__ */ jsx("span", {
				style: badgeStyle,
				children: badge
			})
		]
	});
}
//#endregion
//#region molecules/QuickAction.tsx
/**
* EITELKEIT QuickAction
* ═══════════════════════════════════════════════════════════════════
* Quick action button with keyboard shortcut hint
*/
function QuickAction({ icon, label, shortcut, variant = "default", disabled, style, ...props }) {
	const [isHovered, setIsHovered] = React.useState(false);
	const isDanger = variant === "danger";
	return /* @__PURE__ */ jsxs("button", {
		style: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: "4px",
			padding: "12px 8px",
			background: isHovered ? isDanger ? "rgba(255, 71, 87, 0.1)" : colors.surfaceElevated : colors.void,
			border: `1px solid ${isHovered ? isDanger ? "rgba(255, 71, 87, 0.3)" : colors.borderHover : colors.border}`,
			borderRadius: radii.sm,
			color: isHovered ? isDanger ? colors.danger : colors.textPrimary : colors.textSecondary,
			cursor: disabled ? "not-allowed" : "pointer",
			transition: transitions.all,
			fontFamily: fonts.mono,
			opacity: disabled ? .5 : 1,
			...style
		},
		disabled,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		...props,
		children: [
			/* @__PURE__ */ jsx("span", {
				style: { fontSize: "20px" },
				children: icon
			}),
			/* @__PURE__ */ jsx("span", {
				style: {
					fontSize: "10px",
					textTransform: "uppercase"
				},
				children: label
			}),
			shortcut && /* @__PURE__ */ jsx("span", {
				style: {
					fontSize: "9px",
					color: colors.textMuted,
					background: colors.surface,
					padding: "2px 4px",
					borderRadius: "3px"
				},
				children: shortcut
			})
		]
	});
}
//#endregion
//#region molecules/ProgressBar.tsx
const variantColors = {
	mint: {
		bg: "rgba(57, 255, 159, 0.2)",
		fill: colors.mint,
		glow: "0 0 8px rgba(57, 255, 159, 0.4)"
	},
	warning: {
		bg: "rgba(255, 184, 0, 0.2)",
		fill: colors.warning,
		glow: "0 0 8px rgba(255, 184, 0, 0.4)"
	},
	danger: {
		bg: "rgba(255, 71, 87, 0.2)",
		fill: colors.danger,
		glow: "0 0 8px rgba(255, 71, 87, 0.4)"
	},
	info: {
		bg: "rgba(100, 150, 255, 0.2)",
		fill: colors.info,
		glow: "0 0 8px rgba(100, 150, 255, 0.4)"
	}
};
const sizes = {
	sm: 4,
	md: 6,
	lg: 8
};
function ProgressBar({ value, variant, size, showLabel, label, animated, style, className, ...props }) {
	const v = variant ?? "mint";
	const s = size ?? "md";
	const show = showLabel ?? false;
	const anim = animated ?? true;
	const clampedValue = Math.min(100, Math.max(0, value));
	const colorSet = variantColors[v];
	const height = sizes[s];
	const containerStyle = {
		width: "100%",
		...style
	};
	const trackStyle = {
		width: "100%",
		height: `${height}px`,
		background: colorSet.bg,
		borderRadius: radii.sm,
		overflow: "hidden",
		position: "relative"
	};
	const fillStyle = {
		height: "100%",
		width: `${clampedValue}%`,
		background: colorSet.fill,
		borderRadius: radii.sm,
		transition: anim ? `width ${transitions.slow}` : "none",
		boxShadow: clampedValue > 0 ? colorSet.glow : "none"
	};
	const labelStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		fontFamily: fonts.mono,
		fontSize: "11px",
		color: colors.textMuted,
		marginBottom: "6px"
	};
	return /* @__PURE__ */ jsxs("div", {
		style: containerStyle,
		className: className ?? void 0,
		...props,
		children: [(show || label) && /* @__PURE__ */ jsxs("div", {
			style: labelStyle,
			children: [label && /* @__PURE__ */ jsx("span", { children: label }), show && /* @__PURE__ */ jsxs("span", { children: [clampedValue, "%"] })]
		}), /* @__PURE__ */ jsx("div", {
			style: trackStyle,
			children: /* @__PURE__ */ jsx("div", { style: fillStyle })
		})]
	});
}
//#endregion
//#region molecules/EmptyState.tsx
/**
* EITELKEIT EmptyState
* ═══════════════════════════════════════════════════════════════════
* Empty state molecule for displaying when content is unavailable
*/
function ActionButton({ label, onClick }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		style: {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			fontFamily: fonts.sans,
			fontSize: fontSizes.md,
			fontWeight: fontWeights.semibold,
			background: colors.mint,
			color: colors.void,
			border: "none",
			borderRadius: radii.md,
			padding: `${spacing.sm} ${spacing.lg}`,
			cursor: "pointer",
			transition: transitions.all,
			outline: "none",
			...isHovered && {
				background: colors.mintDim,
				boxShadow: shadows.mintGlow
			}
		},
		onClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		children: label
	});
}
function EmptyState({ title, description, icon, action, style }) {
	const containerStyle = {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
		padding: spacing.xl,
		gap: spacing.md,
		...style ?? {}
	};
	const iconContainerStyle = {
		color: colors.textMuted,
		marginBottom: spacing.sm
	};
	const titleStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.semibold,
		color: colors.textPrimary,
		margin: 0
	};
	const descriptionStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.normal,
		color: colors.textMuted,
		margin: 0,
		maxWidth: "320px"
	};
	return /* @__PURE__ */ jsxs("div", {
		style: containerStyle,
		children: [
			icon && /* @__PURE__ */ jsx("div", {
				style: iconContainerStyle,
				children: icon
			}),
			/* @__PURE__ */ jsx("h3", {
				style: titleStyle,
				children: title
			}),
			description && /* @__PURE__ */ jsx("p", {
				style: descriptionStyle,
				children: description
			}),
			action && /* @__PURE__ */ jsx(ActionButton, {
				label: action.label,
				onClick: action.onClick
			})
		]
	});
}
EmptyState.displayName = "EmptyState";
//#endregion
//#region molecules/MemberStack.tsx
/**
* EITELKEIT MemberStack
* ═══════════════════════════════════════════════════════════════════
* Overlapping avatars with overflow count - perfect for showing collaborators
*/
const sizeMap$1 = {
	sm: {
		avatar: "xs",
		dimension: 24,
		overlap: 8,
		badge: 20,
		fontSize: fontSizes.xs
	},
	md: {
		avatar: "sm",
		dimension: 32,
		overlap: 10,
		badge: 24,
		fontSize: fontSizes.sm
	},
	lg: {
		avatar: "md",
		dimension: 40,
		overlap: 12,
		badge: 28,
		fontSize: fontSizes.md
	}
};
function MemberStack({ members, max, size, onClick, style, ...props }) {
	const [isHovered, setIsHovered] = useState(false);
	const sz = size ?? "md";
	const maxCount = max ?? members.length;
	const sizeConfig = sizeMap$1[sz];
	const visibleMembers = members.slice(0, maxCount);
	const overflowCount = members.length - maxCount;
	const hasOverflow = overflowCount > 0;
	const containerStyle = {
		display: "inline-flex",
		alignItems: "center",
		cursor: onClick ? "pointer" : "default",
		transition: transitions.all,
		borderRadius: radii.full,
		padding: "2px",
		...onClick && isHovered ? { background: colors.surfaceElevated } : {},
		...style
	};
	const stackStyle = {
		display: "flex",
		alignItems: "center",
		flexDirection: "row"
	};
	const avatarWrapperStyle = (index) => ({
		marginLeft: index === 0 ? 0 : -sizeConfig.overlap,
		position: "relative",
		zIndex: visibleMembers.length - index,
		border: `2px solid ${colors.void}`,
		borderRadius: radii.full,
		background: colors.void,
		transition: transitions.all
	});
	const overflowBadgeStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginLeft: -sizeConfig.overlap,
		width: sizeConfig.badge,
		height: sizeConfig.badge,
		borderRadius: radii.full,
		background: colors.surface,
		border: `2px solid ${colors.void}`,
		fontFamily: fonts.sans,
		fontSize: sizeConfig.fontSize,
		fontWeight: fontWeights.semibold,
		color: colors.textSecondary,
		zIndex: 0,
		transition: transitions.all,
		...isHovered ? {
			background: colors.mintGlow,
			color: colors.mint
		} : {}
	};
	const handleMouseEnter = () => setIsHovered(true);
	const handleMouseLeave = () => setIsHovered(false);
	const handleClick = () => {
		if (onClick) onClick();
	};
	const handleKeyDown = (e) => {
		if (onClick && (e.key === "Enter" || e.key === " ")) {
			e.preventDefault();
			onClick();
		}
	};
	if (members.length === 0) return null;
	return /* @__PURE__ */ jsx("div", {
		style: containerStyle,
		onClick: handleClick,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onKeyDown: handleKeyDown,
		role: onClick ? "button" : void 0,
		tabIndex: onClick ? 0 : void 0,
		"aria-label": onClick ? `${members.length} member${members.length === 1 ? "" : "s"}. Click to manage.` : `${members.length} member${members.length === 1 ? "" : "s"}`,
		...props,
		children: /* @__PURE__ */ jsxs("div", {
			style: stackStyle,
			children: [visibleMembers.map((member, index) => /* @__PURE__ */ jsx("div", {
				style: avatarWrapperStyle(index),
				children: /* @__PURE__ */ jsx(Avatar, {
					src: member.avatarUrl,
					alt: member.name,
					name: member.name,
					size: sizeConfig.avatar,
					status: null,
					glow: null
				})
			}, member.id)), hasOverflow && /* @__PURE__ */ jsxs("span", {
				style: overflowBadgeStyle,
				"data-testid": "overflow-badge",
				children: ["+", overflowCount]
			})]
		})
	});
}
//#endregion
//#region molecules/NotificationBell.tsx
/**
* EITELKEIT NotificationBell
* ═══════════════════════════════════════════════════════════════════
* Notification bell with unread badge and dropdown list
*/
const sizeMap = {
	sm: {
		icon: 16,
		badge: 14,
		badgeFont: 9
	},
	md: {
		icon: 20,
		badge: 18,
		badgeFont: 10
	},
	lg: {
		icon: 24,
		badge: 20,
		badgeFont: 11
	}
};
function BellIcon({ size, color }) {
	return /* @__PURE__ */ jsxs("svg", {
		width: size,
		height: size,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: color,
		strokeWidth: 2,
		strokeLinecap: "round",
		strokeLinejoin: "round",
		children: [/* @__PURE__ */ jsx("path", { d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" }), /* @__PURE__ */ jsx("path", { d: "M13.73 21a2 2 0 0 1-3.46 0" })]
	});
}
function NotificationListItem({ notification, onClick }) {
	const [isHovered, setIsHovered] = useState(false);
	const iconColor = {
		MENTION: colors.mint,
		DM: colors.info,
		REACTION: colors.warning,
		PROJECT_INVITE: colors.sub
	}[notification.type];
	const formatTime = (date) => {
		const diff = (/* @__PURE__ */ new Date()).getTime() - date.getTime();
		const minutes = Math.floor(diff / 6e4);
		const hours = Math.floor(diff / 36e5);
		const days = Math.floor(diff / 864e5);
		if (minutes < 1) return "just now";
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days < 7) return `${days}d ago`;
		return date.toLocaleDateString();
	};
	return /* @__PURE__ */ jsxs("div", {
		onClick: () => onClick?.(),
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		"data-testid": "notification-item",
		style: {
			display: "flex",
			alignItems: "flex-start",
			gap: spacing.sm,
			padding: spacing.md,
			cursor: onClick !== null ? "pointer" : "default",
			background: isHovered ? colors.surfaceElevated : "transparent",
			borderBottom: `1px solid ${colors.border}`,
			transition: transitions.all,
			opacity: notification.read ? .6 : 1
		},
		children: [
			!notification.read && /* @__PURE__ */ jsx("div", { style: {
				width: 6,
				height: 6,
				borderRadius: radii.full,
				background: iconColor,
				marginTop: 6,
				flexShrink: 0
			} }),
			notification.read && /* @__PURE__ */ jsx("div", { style: {
				width: 6,
				flexShrink: 0
			} }),
			/* @__PURE__ */ jsxs("div", {
				style: {
					flex: 1,
					minWidth: 0
				},
				children: [
					/* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.sans,
							fontSize: fontSizes.sm,
							fontWeight: 500,
							color: colors.textPrimary,
							marginBottom: 2,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap"
						},
						children: notification.title
					}),
					/* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.sans,
							fontSize: fontSizes.xs,
							color: colors.textMuted,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap"
						},
						children: notification.body
					}),
					/* @__PURE__ */ jsx("div", {
						style: {
							fontFamily: fonts.mono,
							fontSize: fontSizes.xs,
							color: colors.textMuted,
							marginTop: 4
						},
						children: formatTime(notification.createdAt)
					})
				]
			})
		]
	});
}
const NotificationBell = forwardRef(function NotificationBell({ notifications, unreadCount, onNotificationClick, onMarkAllRead, onOpen, size, style }, ref) {
	const [isOpen, setIsOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const dropdownRef = useRef(null);
	const sizeConfig = sizeMap[size ?? "md"];
	const badgeText = unreadCount > 99 ? "99+" : String(unreadCount);
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current !== null && !dropdownRef.current.contains(event.target)) setIsOpen(false);
		};
		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [isOpen]);
	const handleToggle = () => {
		const newState = !isOpen;
		setIsOpen(newState);
		if (newState && onOpen !== null) onOpen();
	};
	const handleNotificationClick = (notification) => {
		if (onNotificationClick !== null) onNotificationClick(notification);
		setIsOpen(false);
	};
	return /* @__PURE__ */ jsxs("div", {
		ref,
		style: {
			position: "relative",
			...style
		},
		children: [/* @__PURE__ */ jsxs("button", {
			type: "button",
			onClick: handleToggle,
			onMouseEnter: () => setIsHovered(true),
			onMouseLeave: () => setIsHovered(false),
			"aria-label": `Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`,
			"aria-expanded": isOpen,
			"aria-haspopup": "true",
			"data-testid": "notification-bell",
			style: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: sizeConfig.icon + 16,
				height: sizeConfig.icon + 16,
				padding: 0,
				margin: 0,
				border: "none",
				borderRadius: radii.md,
				background: isHovered ? colors.surface : "transparent",
				cursor: "pointer",
				position: "relative",
				transition: transitions.all
			},
			children: [/* @__PURE__ */ jsx(BellIcon, {
				size: sizeConfig.icon,
				color: isHovered ? colors.textPrimary : colors.textSecondary
			}), unreadCount > 0 && /* @__PURE__ */ jsx("div", {
				"data-testid": "badge",
				style: {
					position: "absolute",
					top: 0,
					right: 0,
					minWidth: sizeConfig.badge,
					height: sizeConfig.badge,
					padding: "0 4px",
					borderRadius: radii.full,
					background: colors.danger,
					color: colors.textPrimary,
					fontFamily: fonts.mono,
					fontSize: sizeConfig.badgeFont,
					fontWeight: 600,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					boxShadow: shadows.sm
				},
				children: badgeText
			})]
		}), isOpen && /* @__PURE__ */ jsxs("div", {
			ref: dropdownRef,
			role: "menu",
			"data-testid": "notification-dropdown",
			style: {
				position: "absolute",
				top: "100%",
				right: 0,
				marginTop: spacing.xs,
				width: 320,
				maxHeight: 400,
				background: colors.surface,
				border: `1px solid ${colors.border}`,
				borderRadius: radii.lg,
				boxShadow: shadows.lg,
				zIndex: zIndices.dropdown,
				overflow: "hidden"
			},
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: spacing.md,
					borderBottom: `1px solid ${colors.border}`
				},
				children: [/* @__PURE__ */ jsx("span", {
					style: {
						fontFamily: fonts.sans,
						fontSize: fontSizes.sm,
						fontWeight: 600,
						color: colors.textPrimary,
						textTransform: "uppercase",
						letterSpacing: "0.05em"
					},
					children: "Notifications"
				}), unreadCount > 0 && onMarkAllRead !== null && /* @__PURE__ */ jsx(MarkAllReadButton, { onClick: onMarkAllRead })]
			}), /* @__PURE__ */ jsx("div", {
				style: {
					maxHeight: 320,
					overflowY: "auto"
				},
				children: notifications.length === 0 ? /* @__PURE__ */ jsx("div", {
					style: {
						padding: spacing.xl,
						textAlign: "center",
						fontFamily: fonts.sans,
						fontSize: fontSizes.sm,
						color: colors.textMuted
					},
					children: "No notifications"
				}) : notifications.map((notification) => /* @__PURE__ */ jsx(NotificationListItem, {
					notification,
					onClick: () => handleNotificationClick(notification)
				}, notification.id))
			})]
		})]
	});
});
function MarkAllReadButton({ onClick }) {
	const [isHovered, setIsHovered] = useState(false);
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		onClick,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		"data-testid": "mark-all-read-button",
		style: {
			padding: "4px 8px",
			margin: 0,
			border: "none",
			borderRadius: radii.sm,
			background: isHovered ? colors.mintGlow : "transparent",
			color: colors.mint,
			fontFamily: fonts.sans,
			fontSize: fontSizes.xs,
			fontWeight: 500,
			cursor: "pointer",
			transition: transitions.all
		},
		children: "Mark all read"
	});
}
//#endregion
//#region molecules/ReactionPicker.tsx
/**
* EITELKEIT ReactionPicker
* =====================================================
* A compact emoji picker for message reactions.
* Shows quick reaction options in a row with selection state.
*/
/** Default quick reaction emojis */
const DEFAULT_EMOJIS = [
	"👍",
	"❤️",
	"😂",
	"😮",
	"😢",
	"🔥"
];
/**
* ReactionPicker - Quick emoji reaction selector
*
* Displays a horizontal row of emoji buttons for quick message reactions.
* Selected emojis are highlighted with a mint glow.
*
* @example
* ```tsx
* <ReactionPicker
*   selectedEmojis={['thumbsup']}
*   onReact={(emoji) => handleReaction(emoji)}
*   onClose={() => setPickerOpen(false)}
*   emojis={null}
*   compact={null}
* />
* ```
*/
const ReactionPicker = forwardRef(function ReactionPicker({ selectedEmojis, onReact, onClose, emojis, compact, style, ...props }, ref) {
	const emojiList = emojis ?? DEFAULT_EMOJIS;
	const isCompact = compact ?? false;
	return /* @__PURE__ */ jsxs("div", {
		ref,
		style: {
			display: "inline-flex",
			alignItems: "center",
			gap: isCompact ? spacing.xs : spacing.sm,
			background: colors.surface,
			border: `1px solid ${colors.border}`,
			borderRadius: radii.full,
			padding: isCompact ? `${spacing.xs} ${spacing.sm}` : `${spacing.sm} ${spacing.md}`,
			boxShadow: shadows.md,
			...style
		},
		role: "toolbar",
		"aria-label": "Reaction picker",
		"data-testid": "reaction-picker",
		...props,
		children: [emojiList.map((emoji) => /* @__PURE__ */ jsx(EmojiButton, {
			emoji,
			isSelected: selectedEmojis.includes(emoji),
			onClick: () => onReact(emoji),
			compact: isCompact
		}, emoji)), /* @__PURE__ */ jsx(CloseButton, {
			onClick: onClose,
			compact: isCompact
		})]
	});
});
function EmojiButton({ emoji, isSelected, onClick, compact }) {
	const [isHovered, setIsHovered] = useState(false);
	const size = compact ? "28px" : "32px";
	const fontSize = compact ? fontSizes.base : fontSizes.lg;
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: size,
			height: size,
			padding: 0,
			background: isSelected ? colors.mintGlow : isHovered ? colors.surfaceElevated : "transparent",
			border: isSelected ? `1px solid ${colors.mint}` : "1px solid transparent",
			borderRadius: radii.full,
			cursor: "pointer",
			transition: transitions.all,
			fontSize,
			lineHeight: 1,
			boxShadow: isSelected ? shadows.glow : "none"
		},
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onClick,
		"aria-label": `React with ${emoji}`,
		"aria-pressed": isSelected,
		children: emoji
	});
}
function CloseButton({ onClick, compact }) {
	const [isHovered, setIsHovered] = useState(false);
	const size = compact ? "24px" : "28px";
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		style: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: size,
			height: size,
			padding: 0,
			marginLeft: compact ? spacing.xs : spacing.sm,
			background: isHovered ? colors.surfaceElevated : "transparent",
			border: `1px solid ${colors.border}`,
			borderRadius: radii.full,
			cursor: "pointer",
			transition: transitions.all,
			color: colors.textMuted,
			fontFamily: fonts.mono,
			fontSize: fontSizes.sm,
			lineHeight: 1
		},
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onClick,
		"aria-label": "Close reaction picker",
		children: "×"
	});
}
//#endregion
//#region molecules/MessageReactions.tsx
/**
* EITELKEIT MessageReactions
* =====================================================
* Displays grouped reactions under a message.
* Shows emoji with count, expandable to show who reacted.
*/
function groupReactions(reactions, currentUserId) {
	const groups = /* @__PURE__ */ new Map();
	for (const reaction of reactions) {
		const existing = groups.get(reaction.emoji);
		if (existing !== void 0) {
			existing.count++;
			existing.users.push({
				userId: reaction.userId,
				userName: reaction.userName
			});
			if (reaction.userId === currentUserId) existing.hasCurrentUser = true;
		} else groups.set(reaction.emoji, {
			emoji: reaction.emoji,
			count: 1,
			users: [{
				userId: reaction.userId,
				userName: reaction.userName
			}],
			hasCurrentUser: reaction.userId === currentUserId
		});
	}
	return Array.from(groups.values());
}
/**
* MessageReactions - Display reactions grouped by emoji
*
* Shows emoji badges with counts under messages.
* Hover to see who reacted, click to add/remove your reaction.
*
* @example
* ```tsx
* <MessageReactions
*   reactions={[
*     { emoji: 'thumbsup', userId: '1', userName: 'Alice' },
*     { emoji: 'thumbsup', userId: '2', userName: 'Bob' },
*   ]}
*   currentUserId="1"
*   onReactionClick={(emoji) => toggleReaction(emoji)}
*   compact={null}
* />
* ```
*/
const MessageReactions = forwardRef(function MessageReactions({ reactions, currentUserId, onReactionClick, compact, style, ...props }, ref) {
	const isCompact = compact ?? false;
	const grouped = groupReactions(reactions, currentUserId);
	if (grouped.length === 0) return null;
	return /* @__PURE__ */ jsx("div", {
		ref,
		style: {
			display: "flex",
			flexWrap: "wrap",
			gap: isCompact ? spacing.xs : spacing.sm,
			...style
		},
		role: "group",
		"aria-label": "Message reactions",
		...props,
		children: grouped.map((group) => /* @__PURE__ */ jsx(ReactionBadge, {
			group,
			onClick: () => onReactionClick(group.emoji),
			compact: isCompact
		}, group.emoji))
	});
});
function ReactionBadge({ group, onClick, compact }) {
	const [isHovered, setIsHovered] = useState(false);
	const padding = compact ? `2px ${spacing.xs}` : `${spacing.xs} ${spacing.sm}`;
	const fontSize = compact ? fontSizes.xs : fontSizes.sm;
	const badgeStyle = {
		display: "inline-flex",
		alignItems: "center",
		gap: spacing.xs,
		padding,
		background: group.hasCurrentUser ? colors.mintGlow : isHovered ? colors.surfaceElevated : colors.surface,
		border: group.hasCurrentUser ? `1px solid ${colors.mint}` : `1px solid ${colors.border}`,
		borderRadius: radii.full,
		cursor: "pointer",
		transition: transitions.all,
		position: "relative"
	};
	const emojiStyle = {
		fontSize: compact ? fontSizes.sm : fontSizes.base,
		lineHeight: 1
	};
	const countStyle = {
		fontFamily: fonts.mono,
		fontSize,
		fontWeight: fontWeights.medium,
		color: group.hasCurrentUser ? colors.mint : colors.textSecondary
	};
	const tooltipStyle = {
		position: "absolute",
		bottom: "100%",
		left: "50%",
		transform: "translateX(-50%)",
		marginBottom: spacing.xs,
		padding: `${spacing.xs} ${spacing.sm}`,
		background: colors.surfaceElevated,
		border: `1px solid ${colors.border}`,
		borderRadius: radii.sm,
		whiteSpace: "nowrap",
		fontSize: fontSizes.xs,
		fontFamily: fonts.sans,
		color: colors.textSecondary,
		opacity: isHovered ? 1 : 0,
		visibility: isHovered ? "visible" : "hidden",
		transition: transitions.opacity,
		zIndex: 100,
		pointerEvents: "none"
	};
	const tooltipText = group.users.length <= 3 ? group.users.map((u) => u.userName).join(", ") : `${group.users.slice(0, 2).map((u) => u.userName).join(", ")} and ${group.users.length - 2} more`;
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		style: badgeStyle,
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		onClick,
		"aria-label": `${group.emoji} reaction by ${tooltipText}${group.hasCurrentUser ? " (click to remove)" : " (click to add)"}`,
		"data-testid": "reaction-badge",
		children: [
			/* @__PURE__ */ jsx("span", {
				style: emojiStyle,
				children: group.emoji
			}),
			/* @__PURE__ */ jsx("span", {
				style: countStyle,
				"data-testid": "reaction-count",
				children: group.count
			}),
			/* @__PURE__ */ jsx("span", {
				style: tooltipStyle,
				children: tooltipText
			})
		]
	});
}
//#endregion
//#region molecules/ReadReceiptIndicator.tsx
/**
* EITELKEIT ReadReceiptIndicator
* =====================================================
* Shows message delivery and read status.
* Single check for sent, double check for read.
*/
/**
* ReadReceiptIndicator - Message delivery status indicator
*
* Displays checkmarks to indicate message delivery state:
* - "Sending..." text while pending
* - Single checkmark when sent
* - Double checkmark when delivered
* - Double checkmark (highlighted) when read, with reader avatars on hover
*
* @example
* ```tsx
* <ReadReceiptIndicator
*   status="read"
*   readers={[{ userId: '1', userName: 'Alice', avatarUrl: null, readAt: new Date() }]}
*   compact={null}
*   showTimestamp={null}
* />
* ```
*/
const ReadReceiptIndicator = forwardRef(function ReadReceiptIndicator({ status, readers, compact, showTimestamp, style, ...props }, ref) {
	const [isHovered, setIsHovered] = useState(false);
	const isCompact = compact ?? false;
	const shouldShowTimestamp = showTimestamp ?? false;
	const readerList = readers ?? [];
	return /* @__PURE__ */ jsxs("div", {
		ref,
		style: {
			display: "inline-flex",
			alignItems: "center",
			gap: spacing.xs,
			position: "relative",
			cursor: readerList.length > 0 ? "pointer" : "default",
			...style
		},
		onMouseEnter: () => setIsHovered(true),
		onMouseLeave: () => setIsHovered(false),
		role: "status",
		"aria-label": getAriaLabel(status, readerList),
		...props,
		children: [
			/* @__PURE__ */ jsx(StatusIcon, {
				status,
				compact: isCompact
			}),
			shouldShowTimestamp && status === "read" && readerList.length > 0 && /* @__PURE__ */ jsx(Timestamp, {
				date: readerList[0]?.readAt ?? null,
				compact: isCompact
			}),
			isHovered && readerList.length > 0 && /* @__PURE__ */ jsx(ReaderTooltip, { readers: readerList })
		]
	});
});
/** Get accessible label for status */
function getAriaLabel(status, readers) {
	switch (status) {
		case "sending": return "Message sending";
		case "sent": return "Message sent";
		case "delivered": return "Message delivered";
		case "read":
			if (readers.length === 0) return "Message read";
			if (readers.length === 1) return `Read by ${readers[0]?.userName ?? "someone"}`;
			return `Read by ${readers.length} people`;
	}
}
function StatusIcon({ status, compact }) {
	const size = compact ? "12px" : "14px";
	const textSize = compact ? fontSizes.xs : fontSizes.sm;
	if (status === "sending") return /* @__PURE__ */ jsx("span", {
		style: {
			fontFamily: fonts.mono,
			fontSize: textSize,
			color: colors.textMuted,
			fontStyle: "italic"
		},
		children: "Sending..."
	});
	return /* @__PURE__ */ jsx("span", {
		style: {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			color: status === "read" ? colors.mint : colors.textMuted,
			fontSize: size,
			lineHeight: 1,
			transition: transitions.colors
		},
		children: status === "sent" ? "✓" : "✓✓"
	});
}
function Timestamp({ date, compact }) {
	if (date === null) return null;
	return /* @__PURE__ */ jsx("span", {
		style: {
			fontFamily: fonts.mono,
			fontSize: compact ? fontSizes.xs : fontSizes.sm,
			color: colors.textMuted
		},
		children: date.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit"
		})
	});
}
function ReaderTooltip({ readers }) {
	const tooltipStyle = {
		position: "absolute",
		bottom: "100%",
		right: 0,
		marginBottom: spacing.xs,
		padding: spacing.sm,
		background: colors.surfaceElevated,
		border: `1px solid ${colors.border}`,
		borderRadius: radii.md,
		minWidth: "120px",
		maxWidth: "200px",
		zIndex: 100
	};
	const headerStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.xs,
		fontWeight: 600,
		color: colors.textMuted,
		marginBottom: spacing.xs,
		textTransform: "uppercase",
		letterSpacing: "0.05em"
	};
	const listStyle = {
		display: "flex",
		flexDirection: "column",
		gap: spacing.xs
	};
	const maxDisplay = 5;
	const displayReaders = readers.slice(0, maxDisplay);
	const remaining = readers.length - maxDisplay;
	return /* @__PURE__ */ jsxs("div", {
		style: tooltipStyle,
		children: [/* @__PURE__ */ jsx("div", {
			style: headerStyle,
			children: "Read by"
		}), /* @__PURE__ */ jsxs("div", {
			style: listStyle,
			children: [displayReaders.map((reader) => /* @__PURE__ */ jsx(ReaderRow, { reader }, reader.userId)), remaining > 0 && /* @__PURE__ */ jsx(MoreReadersRow, { count: remaining })]
		})]
	});
}
function ReaderRow({ reader }) {
	const rowStyle = {
		display: "flex",
		alignItems: "center",
		gap: spacing.sm
	};
	const avatarStyle = {
		width: "20px",
		height: "20px",
		borderRadius: radii.full,
		background: colors.surface,
		border: `1px solid ${colors.border}`,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		overflow: "hidden",
		flexShrink: 0
	};
	const avatarImgStyle = {
		width: "100%",
		height: "100%",
		objectFit: "cover"
	};
	const initialsStyle = {
		fontFamily: fonts.mono,
		fontSize: fontSizes.xs,
		color: colors.textMuted
	};
	const nameStyle = {
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		color: colors.textPrimary,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis"
	};
	const timeStyle = {
		fontFamily: fonts.mono,
		fontSize: fontSizes.xs,
		color: colors.textMuted,
		marginLeft: "auto",
		flexShrink: 0
	};
	const initials = reader.userName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
	const timeString = reader.readAt.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit"
	});
	return /* @__PURE__ */ jsxs("div", {
		style: rowStyle,
		children: [
			/* @__PURE__ */ jsx("div", {
				style: avatarStyle,
				children: reader.avatarUrl !== null ? /* @__PURE__ */ jsx("img", {
					src: reader.avatarUrl,
					alt: reader.userName,
					style: avatarImgStyle
				}) : /* @__PURE__ */ jsx("span", {
					style: initialsStyle,
					children: initials
				})
			}),
			/* @__PURE__ */ jsx("span", {
				style: nameStyle,
				children: reader.userName
			}),
			/* @__PURE__ */ jsx("span", {
				style: timeStyle,
				children: timeString
			})
		]
	});
}
function MoreReadersRow({ count }) {
	return /* @__PURE__ */ jsxs("div", {
		style: {
			fontFamily: fonts.sans,
			fontSize: fontSizes.xs,
			color: colors.textMuted,
			paddingTop: spacing.xs,
			borderTop: `1px solid ${colors.border}`
		},
		children: [
			"+",
			count,
			" more"
		]
	});
}
//#endregion
//#region molecules/SplitPanel.tsx
/**
* EITELKEIT SplitPanel
* ═══════════════════════════════════════════════════════════════════
* Resizable split-pane container. Horizontal or vertical orientation.
* Draggable divider with touch support.
* STELLWERK uses this to stack terminal / editor / markdown panels.
*
* M0: API + layout skeleton only. Drag handle wiring in M1.
*/
function SplitPanel({ orientation, defaultRatio, minRatio, maxRatio, dividerSize, first, second, style }) {
	const [ratio] = useState(defaultRatio ?? .5);
	const isHorizontal = orientation === "horizontal";
	const divider = dividerSize ?? 6;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: isHorizontal ? "row" : "column",
			width: "100%",
			height: "100%",
			overflow: "hidden",
			...style ?? {}
		},
		children: [
			/* @__PURE__ */ jsx("div", {
				style: {
					flex: `${ratio} 1 0`,
					minWidth: 0,
					minHeight: 0,
					overflow: "auto"
				},
				children: first
			}),
			/* @__PURE__ */ jsx("div", {
				role: "separator",
				"aria-orientation": isHorizontal ? "vertical" : "horizontal",
				style: {
					width: isHorizontal ? `${divider}px` : "100%",
					height: isHorizontal ? "100%" : `${divider}px`,
					background: colors.border,
					cursor: isHorizontal ? "col-resize" : "row-resize",
					flexShrink: 0
				}
			}),
			/* @__PURE__ */ jsx("div", {
				style: {
					flex: `${1 - ratio} 1 0`,
					minWidth: 0,
					minHeight: 0,
					overflow: "auto"
				},
				children: second
			})
		]
	});
}
//#endregion
//#region molecules/SessionTabBar.tsx
const TYPE_GLYPH = {
	terminal: "$_",
	editor: "</>",
	markdown: "MD",
	agent: "AI",
	board: "⊞",
	monorepo: "⬡"
};
const STATE_COLOR = {
	idle: colors.textMuted,
	running: colors.mint,
	waiting: colors.mint,
	error: colors.danger,
	dirty: colors.warning
};
function SessionTabBar({ tabs, onSelect, onClose, onNewTab, onRename, onReorder, style }) {
	return /* @__PURE__ */ jsxs("div", {
		role: "tablist",
		style: {
			display: "flex",
			alignItems: "stretch",
			gap: "2px",
			padding: "4px",
			background: colors.voidLight,
			borderBottom: `1px solid ${colors.border}`,
			overflowX: "auto",
			flexShrink: 0,
			fontFamily: fonts.mono,
			fontSize: "13px",
			...style ?? {}
		},
		children: [tabs.map((tab) => /* @__PURE__ */ jsxs("div", {
			role: "tab",
			"aria-selected": tab.active,
			onClick: () => onSelect(tab.id),
			style: {
				display: "flex",
				alignItems: "center",
				gap: "8px",
				padding: "6px 10px",
				borderRadius: "4px",
				background: tab.active ? colors.surface : "transparent",
				border: `1px solid ${tab.active ? colors.mint : "transparent"}`,
				color: tab.active ? colors.text : colors.textMuted,
				cursor: "pointer",
				whiteSpace: "nowrap"
			},
			children: [
				/* @__PURE__ */ jsx("span", {
					style: {
						color: colors.textMuted,
						fontSize: "11px"
					},
					children: TYPE_GLYPH[tab.type]
				}),
				/* @__PURE__ */ jsx("span", {
					"aria-label": `state: ${tab.state}`,
					style: {
						width: "6px",
						height: "6px",
						borderRadius: "50%",
						background: STATE_COLOR[tab.state],
						flexShrink: 0
					}
				}),
				/* @__PURE__ */ jsx("span", { children: tab.label }),
				/* @__PURE__ */ jsx("button", {
					type: "button",
					"aria-label": `close ${tab.label}`,
					onClick: (ev) => {
						ev.stopPropagation();
						onClose(tab.id);
					},
					style: {
						background: "transparent",
						border: "none",
						color: colors.textMuted,
						cursor: "pointer",
						padding: "0 2px",
						fontSize: "14px",
						lineHeight: 1
					},
					children: "×"
				})
			]
		}, tab.id)), /* @__PURE__ */ jsx("button", {
			type: "button",
			"aria-label": "new tab",
			onClick: () => onNewTab("terminal"),
			style: {
				padding: "6px 12px",
				background: "transparent",
				border: `1px dashed ${colors.border}`,
				borderRadius: "4px",
				color: colors.textMuted,
				cursor: "pointer",
				fontFamily: fonts.mono
			},
			children: "+"
		})]
	});
}
//#endregion
//#region molecules/MobileKeyboardBar.tsx
const MODE_KEYS = {
	shell: [
		{
			label: "Esc",
			id: "Escape",
			width: null
		},
		{
			label: "Tab",
			id: "Tab",
			width: null
		},
		{
			label: "Ctrl",
			id: "Control",
			width: null
		},
		{
			label: "↑",
			id: "ArrowUp",
			width: null
		},
		{
			label: "↓",
			id: "ArrowDown",
			width: null
		},
		{
			label: "←",
			id: "ArrowLeft",
			width: null
		},
		{
			label: "→",
			id: "ArrowRight",
			width: null
		},
		{
			label: "|",
			id: "|",
			width: null
		}
	],
	"cc-running": [
		{
			label: "Esc",
			id: "Escape",
			width: null
		},
		{
			label: "⌘+C",
			id: "Cmd+C",
			width: "56px"
		},
		{
			label: "↑",
			id: "ArrowUp",
			width: null
		},
		{
			label: "⇧+↵",
			id: "Shift+Enter",
			width: "56px"
		}
	],
	"cc-waiting": [
		{
			label: "1",
			id: "1",
			width: null
		},
		{
			label: "2",
			id: "2",
			width: null
		},
		{
			label: "3",
			id: "3",
			width: null
		},
		{
			label: "Esc",
			id: "Escape",
			width: null
		},
		{
			label: "↵",
			id: "Enter",
			width: null
		},
		{
			label: "⇧+↵",
			id: "Shift+Enter",
			width: "56px"
		}
	],
	editor: [
		{
			label: "Esc",
			id: "Escape",
			width: null
		},
		{
			label: "Tab",
			id: "Tab",
			width: null
		},
		{
			label: "⌘+S",
			id: "Cmd+S",
			width: null
		},
		{
			label: "⌘+F",
			id: "Cmd+F",
			width: null
		},
		{
			label: "⌘+Z",
			id: "Cmd+Z",
			width: null
		},
		{
			label: "⌘+⇧+Z",
			id: "Cmd+Shift+Z",
			width: "64px"
		}
	]
};
function MobileKeyboardBar({ mode, onKey, keys, style }) {
	const vv = useVisualViewport();
	const activeKeys = keys ?? MODE_KEYS[mode];
	const floatingStyle = vv.isKeyboardVisible ? {
		position: "fixed",
		bottom: `${window.innerHeight - vv.offsetTop - vv.height}px`,
		left: 0,
		right: 0,
		zIndex: 9999
	} : {};
	return /* @__PURE__ */ jsx("div", {
		style: {
			display: "flex",
			gap: "4px",
			padding: "6px",
			background: colors.voidLight,
			borderTop: vv.isKeyboardVisible ? `1px solid ${colors.mint}` : `1px solid ${colors.border}`,
			overflowX: "auto",
			flexShrink: 0,
			...floatingStyle,
			...style ?? {}
		},
		children: activeKeys.map((k) => /* @__PURE__ */ jsx("button", {
			type: "button",
			onClick: () => onKey(k.id),
			style: {
				minWidth: k.width ?? "40px",
				padding: "10px 8px",
				background: colors.surface,
				border: `1px solid ${colors.border}`,
				borderRadius: "6px",
				color: colors.text,
				fontFamily: fonts.mono,
				fontSize: "13px",
				cursor: "pointer",
				touchAction: "manipulation"
			},
			children: k.label
		}, k.id))
	});
}
//#endregion
//#region molecules/PolicyConfirmDialog.tsx
const COOLDOWN_MS = 3e3;
function PolicyConfirmDialog({ command, isOpen, onConfirm, onCancel }) {
	const [reason, setReason] = useState("");
	const [cooldownMs, setCooldownMs] = useState(COOLDOWN_MS);
	useEffect(() => {
		if (!isOpen) return;
		setCooldownMs(COOLDOWN_MS);
		setReason("");
		const iv = setInterval(() => setCooldownMs((p) => {
			if (p <= 100) {
				clearInterval(iv);
				return 0;
			}
			return p - 100;
		}), 100);
		return () => clearInterval(iv);
	}, [isOpen]);
	if (!isOpen) return null;
	const canConfirm = cooldownMs === 0;
	const seconds = Math.ceil(cooldownMs / 1e3);
	return /* @__PURE__ */ jsx("div", {
		style: {
			position: "fixed",
			inset: 0,
			background: "rgba(0,0,0,0.8)",
			zIndex: zIndices.modal,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: spacing[4]
		},
		children: /* @__PURE__ */ jsxs("div", {
			role: "alertdialog",
			"aria-modal": "true",
			style: {
				background: colors.surface,
				border: `1px solid ${colors.danger}44`,
				borderRadius: radii.xl,
				padding: spacing[6],
				maxWidth: 480,
				width: "100%",
				boxShadow: `${shadows.lg}, 0 0 40px ${colors.danger}22`
			},
			children: [
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						alignItems: "center",
						gap: spacing[3],
						marginBottom: spacing[4]
					},
					children: [/* @__PURE__ */ jsx("span", {
						style: {
							fontSize: 24,
							color: colors.danger
						},
						children: "⚠"
					}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
						style: {
							fontSize: 16,
							fontWeight: 700,
							color: colors.danger
						},
						children: "Potentially Dangerous Command"
					}), /* @__PURE__ */ jsx("div", {
						style: {
							fontSize: 12,
							color: colors.textMuted,
							marginTop: 2
						},
						children: "This command may cause irreversible changes"
					})] })]
				}),
				/* @__PURE__ */ jsx("div", {
					style: {
						fontFamily: "var(--font-mono)",
						fontSize: 13,
						color: colors.text,
						background: colors.void,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.sm,
						padding: spacing[3],
						marginBottom: spacing[4],
						wordBreak: "break-all"
					},
					children: command
				}),
				/* @__PURE__ */ jsx("input", {
					type: "text",
					value: reason,
					onChange: (e) => setReason(e.target.value),
					placeholder: "Reason for this command…",
					style: {
						width: "100%",
						background: colors.voidLight,
						border: `1px solid ${colors.border}`,
						borderRadius: radii.sm,
						color: colors.text,
						fontSize: 13,
						fontFamily: "var(--font-mono)",
						padding: spacing[3],
						marginBottom: spacing[4],
						boxSizing: "border-box"
					}
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: spacing[3],
						justifyContent: "flex-end"
					},
					children: [/* @__PURE__ */ jsx("button", {
						type: "button",
						"aria-label": "Cancel",
						onClick: onCancel,
						style: {
							background: "transparent",
							border: `1px solid ${colors.border}`,
							borderRadius: radii.md,
							color: colors.textMuted,
							padding: `${spacing[2]} ${spacing[5]}`,
							cursor: "pointer",
							fontSize: 13,
							fontFamily: "var(--font-mono)",
							minHeight: 44,
							touchAction: "manipulation"
						},
						children: "Cancel"
					}), /* @__PURE__ */ jsx("button", {
						type: "button",
						"aria-label": `Execute anyway${canConfirm ? "" : ` (${seconds}s)`}`,
						disabled: !canConfirm,
						onClick: () => onConfirm(reason),
						style: {
							background: canConfirm ? colors.danger : colors.voidLight,
							border: "none",
							borderRadius: radii.md,
							color: canConfirm ? "#fff" : colors.textMuted,
							padding: `${spacing[2]} ${spacing[5]}`,
							cursor: canConfirm ? "pointer" : "not-allowed",
							fontSize: 13,
							fontFamily: "var(--font-mono)",
							fontWeight: 600,
							minHeight: 44,
							touchAction: "manipulation"
						},
						children: canConfirm ? "Execute anyway" : `Wait ${seconds}s…`
					})]
				})
			]
		})
	});
}
//#endregion
//#region molecules/DrawerBackdrop.tsx
function DrawerBackdrop({ isOpen, onClose, ariaLabel }) {
	const open = isOpen ?? false;
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		"aria-label": ariaLabel ?? "Close drawer",
		...open ? {} : { "aria-hidden": true },
		disabled: !open,
		onClick: () => onClose?.(),
		style: {
			position: "fixed",
			inset: 0,
			zIndex: zIndices.overlay,
			backgroundColor: colors.surfaceOverlay,
			backdropFilter: "blur(4px)",
			WebkitBackdropFilter: "blur(4px)",
			opacity: open ? 1 : 0,
			pointerEvents: open ? "auto" : "none",
			transition: transitions.opacity,
			border: "none",
			padding: 0
		}
	});
}
//#endregion
//#region molecules/BitmaskGrid.tsx
/**
* BitmaskGrid Visualizer (Canvas)
* High-performance Canvas-based visualizer for binary bitmasks (NAFP-signatures).
* Optimized for 60fps at large grids (e.g. 128x128).
*
* Colors: Toxic Mint (#39FF9F) on Void Black.
*/
function BitmaskGrid({ data, rows, cols }) {
	const canvasRef = useRef(null);
	useEffect(() => {
		const canvas = canvasRef.current;
		if (canvas === null || rows === 0 || cols === 0) return;
		const ctx = canvas.getContext("2d", { alpha: false });
		if (ctx === null) return;
		const dpr = window.devicePixelRatio || 1;
		const { width, height } = canvas.getBoundingClientRect();
		const targetWidth = Math.floor(width * dpr);
		const targetHeight = Math.floor(height * dpr);
		if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
			canvas.width = targetWidth;
			canvas.height = targetHeight;
		}
		ctx.save();
		ctx.scale(dpr, dpr);
		ctx.fillStyle = colors.void;
		ctx.fillRect(0, 0, width, height);
		const pixelWidth = width / cols;
		const pixelHeight = height / rows;
		ctx.fillStyle = colors.mint;
		ctx.globalAlpha = .2;
		for (let r = 0; r < rows; r++) {
			const rowOffset = r * cols;
			for (let c = 0; c < cols; c++) {
				const index = rowOffset + c;
				const byteIdx = index >> 3;
				const bitIdx = 7 - (index & 7);
				const byte = data[byteIdx];
				if (byte === void 0) continue;
				if ((byte & 1 << bitIdx) !== 0) ctx.fillRect(c * pixelWidth - .5, r * pixelHeight - .5, pixelWidth + 1, pixelHeight + 1);
			}
		}
		ctx.globalAlpha = 1;
		for (let r = 0; r < rows; r++) {
			const rowOffset = r * cols;
			for (let c = 0; c < cols; c++) {
				const index = rowOffset + c;
				const byteIdx = index >> 3;
				const bitIdx = 7 - (index & 7);
				const byte = data[byteIdx];
				if (byte === void 0) continue;
				if ((byte & 1 << bitIdx) !== 0) ctx.fillRect(c * pixelWidth + .5, r * pixelHeight + .5, pixelWidth - 1, pixelHeight - 1);
			}
		}
		ctx.restore();
	}, [
		data,
		rows,
		cols
	]);
	return /* @__PURE__ */ jsx("canvas", {
		ref: canvasRef,
		style: {
			display: "block",
			width: "100%",
			height: "100%",
			backgroundColor: colors.void
		}
	});
}
//#endregion
export { Breadcrumbs as _, SessionTabBar as a, MessageReactions as c, MemberStack as d, EmptyState as f, Card as g, NavItem as h, MobileKeyboardBar as i, ReactionPicker as l, QuickAction as m, DrawerBackdrop as n, SplitPanel as o, ProgressBar as p, PolicyConfirmDialog as r, ReadReceiptIndicator as s, BitmaskGrid as t, NotificationBell as u };

//# sourceMappingURL=molecules-gQNF-eWR.mjs.map